/**
 * Importar lista — a tela em três atos, e por que são três.
 *
 * 1. RECEBER: texto colado, foto ou PDF. Arquivo passa pela IA e vira linhas.
 *
 * 2. CONFERIR A LEITURA (só quando veio de arquivo — pedido do Lucas): as
 *    linhas aparecem EDITÁVEIS antes de qualquer busca. Entre "o que a IA leu"
 *    e "o que existe no estoque" há dois erros possíveis, e misturá-los numa
 *    etapa só esconde qual aconteceu. Corrigir "cimemto" aqui custa um toque;
 *    descobrir o casamento errado depois custa material errado na obra.
 *
 * 3. CASAR COM O ESTOQUE: cada linha volta como
 *      certa     — já escolhida (código bateu, ou semelhança alta com folga);
 *      duvidosa  — a pessoa escolhe entre os candidatos;
 *      não achei — reportada, fica de fora; vai para a observação do pedido.
 *    A régua é a decisão do Lucas: casa sozinho o óbvio, marca o duvidoso.
 */

import { ErroApi, api } from '../../api.js';
import { escapar, numero } from '../../comum/formato.js';
import { obraAtual, adicionar, registrarNaoEncontrados } from './estado.js';
import { montarBuscaItem, type ItemEscolhido } from '../../comum/busca-item.js';

interface Candidato {
  codigo: string;
  nome: string;
  unidade: string | null;
  saldo: number;
  foto: string | null;
  semelhanca: number;
}

interface LinhaCasada {
  indice: number;
  original: string;
  quantidade: number;
  situacao: 'certo' | 'duvidoso' | 'nao_achei';
  escolhido?: Candidato;
  candidatos: Candidato[];
  viaIa?: string;
}

export function montarLista(raiz: HTMLElement, aoTerminar: () => void): void {
  receber();

  // ------------------------------------------------------------ ato 1: receber

  function receber(): void {
    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>📋 Importar lista de material</h3>
        <p class="dica">
          Cole a lista como ela veio — do WhatsApp, de um e-mail, de qualquer lugar.
          Uma linha por material, com quantidade se tiver.
        </p>
        <textarea id="li-texto" rows="8" placeholder="50 sacos cimento CP2
tubo pvc 100mm — 6
joelo 90 25mm 20un"></textarea>
        <div class="conf-acoes">
          <button class="btn" id="li-conferir">Procurar no estoque</button>
          <label class="btn btn-claro arquivo">
            📷 Foto ou PDF da lista
            <input type="file" id="li-arquivo" accept="image/*,application/pdf" hidden>
          </label>
        </div>
        <p class="aviso" id="li-erro" hidden></p>
        <p class="dica" id="li-estado"></p>
      </section>`;

    const texto = raiz.querySelector<HTMLTextAreaElement>('#li-texto')!;
    const erro = raiz.querySelector<HTMLElement>('#li-erro')!;
    const estado = raiz.querySelector<HTMLElement>('#li-estado')!;

    raiz.querySelector('#li-conferir')!.addEventListener('click', () => {
      const linhas = texto.value
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      if (linhas.length === 0) {
        erro.textContent = 'Cole a lista primeiro — ou envie a foto dela.';
        erro.hidden = false;
        return;
      }
      void casar(linhas);
    });

    raiz.querySelector<HTMLInputElement>('#li-arquivo')!.addEventListener('change', (e) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (f) void lerArquivo(f, estado, erro);
    });
  }

  async function lerArquivo(f: File, estado: HTMLElement, erro: HTMLElement): Promise<void> {
    erro.hidden = true;
    estado.textContent = 'Lendo o arquivo… isso leva alguns segundos.';
    try {
      const corpo = new FormData();
      corpo.append('arquivo', f);
      const r = await fetchLista(corpo);
      conferirLeitura(r.linhas);
    } catch (e) {
      estado.textContent = '';
      erro.textContent = e instanceof ErroApi ? e.message : 'Não consegui ler o arquivo.';
      erro.hidden = false;
    }
  }

  /** O multipart não passa pelo helper `api.post` (que é JSON); vai direto. */
  async function fetchLista(corpo: FormData): Promise<{ linhas: string[] }> {
    return api.enviarMultipart<{ linhas: string[] }>('/api/pedidos/lista/arquivo', corpo);
  }

  // --------------------------------------------- ato 2: conferir a leitura

  function conferirLeitura(linhas: string[]): void {
    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>O que eu li no arquivo</h3>
        <p class="dica">
          Confira antes de procurar no estoque — corrigir uma palavra aqui evita
          casamento errado depois. Linha com <b>?</b> é algo que não deu para ler.
        </p>
        <textarea id="li-texto" rows="${Math.min(14, linhas.length + 2)}">${escapar(linhas.join('\n'))}</textarea>
        <div class="conf-acoes">
          <button class="btn" id="li-conferir">Está certo — procurar no estoque</button>
          <button class="btn btn-claro" id="li-voltar">← Recomeçar</button>
        </div>
      </section>`;

    raiz.querySelector('#li-voltar')!.addEventListener('click', receber);
    raiz.querySelector('#li-conferir')!.addEventListener('click', () => {
      const finais = raiz
        .querySelector<HTMLTextAreaElement>('#li-texto')!
        .value.split('\n')
        .map((l) => l.trim())
        .filter((l) => l && l !== '?');
      if (finais.length) void casar(finais);
    });
  }

  // ------------------------------------------------- ato 3: casar e decidir

  async function casar(linhas: string[]): Promise<void> {
    raiz.innerHTML = `<p class="carregando">Procurando ${linhas.length} linha(s) no estoque…</p>`;
    let resultados: LinhaCasada[];
    try {
      const r = await api.post<{ resultados: LinhaCasada[] }>('/api/pedidos/lista', { linhas });
      resultados = r.resultados;
    } catch (e) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        e instanceof ErroApi ? e.message : 'Não consegui procurar agora.',
      )}</p>`;
      return;
    }
    decidir(resultados);
  }

  function decidir(resultados: LinhaCasada[]): void {
    const certas = resultados.filter((r) => r.situacao === 'certo');
    const duvidosas = resultados.filter((r) => r.situacao === 'duvidoso');
    const perdidas = resultados.filter((r) => r.situacao === 'nao_achei');

    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>O que encontrei</h3>
        <p class="dica">
          <b>${certas.length}</b> certa(s) · <b>${duvidosas.length}</b> para você escolher ·
          <b>${perdidas.length}</b> não encontrada(s).
        </p>
        <div id="li-linhas">
          ${resultados.map(linhaHtml).join('')}
        </div>
        <div class="conf-acoes">
          <button class="btn btn-grande" id="li-carrinho">Adicionar ao carrinho</button>
          <button class="btn btn-claro" id="li-voltar">← Recomeçar</button>
        </div>
        <p class="aviso" id="li-erro" hidden></p>
      </section>`;

    raiz.querySelector('#li-voltar')!.addEventListener('click', receber);
    ligarDecisao(resultados);
  }

  function linhaHtml(r: LinhaCasada): string {
    const cab = `
      <div class="li-original">
        <span class="li-lido">${escapar(r.original)}</span>
        ${r.viaIa ? `<span class="li-ia" title="A busca não achou; a IA sugeriu esta leitura">IA: “${escapar(r.viaIa)}”</span>` : ''}
      </div>`;

    if (r.situacao === 'nao_achei') {
      return `<div class="li-linha li-perdida" data-i="${r.indice}">
          ${cab}
          <div class="li-escolha">
            <p class="li-nada">Não achei nada parecido no estoque. Fica de fora do pedido — e vai
              anotado na observação, para o CD saber que faltou. Ou procure você mesmo:</p>
            <div class="li-buscar" data-buscar="${r.indice}"></div>
          </div>
          <label class="li-qtd" hidden>
            <span>Qtd.</span>
            <input type="number" min="0.001" step="any" value="${r.quantidade}" data-qtd="${r.indice}">
          </label>
        </div>`;
    }

    const escolhido = r.situacao === 'certo' ? r.escolhido! : undefined;
    return `<div class="li-linha ${escolhido ? 'li-certa' : 'li-duvida'}" data-i="${r.indice}">
        ${cab}
        <div class="li-escolha">
          ${
            escolhido
              ? candidatoHtml(escolhido, r.indice, true)
              : `<p class="li-pergunta">Qual destes é?</p>` +
                // O "não vincular" vem PRIMEIRO (02/09, Lucas): é a saída mais
                // comum quando a lista propôs errado, não pode morar no rodapé.
                `<button class="li-nenhum" data-nenhum="${r.indice}">🚫 Não vincular — deixar de fora</button>` +
                r.candidatos.map((c) => candidatoHtml(c, r.indice, false)).join('') +
                `<div class="li-buscar" data-buscar="${r.indice}"></div>`
          }
        </div>
        <label class="li-qtd">
          <span>Qtd.</span>
          <input type="number" min="0.001" step="any" value="${r.quantidade}" data-qtd="${r.indice}">
        </label>
      </div>`;
  }

  function candidatoHtml(c: Candidato, indice: number, escolhido: boolean): string {
    return `
      <button class="li-cand ${escolhido ? 'li-cand-ok' : ''}" data-cand="${indice}" data-cod="${escapar(c.codigo)}" ${escolhido ? 'disabled' : ''}>
        <span class="li-cfoto">${
          c.foto
            ? `<img src="${escapar(api.foto(c.foto) ?? '')}" alt="" loading="lazy">`
            : '<span class="sem">—</span>'
        }</span>
        <span class="li-cnome">
          <b>${escapar(c.nome)}</b>
          <span class="sub-inline">${escapar(c.codigo)} · disponível ${numero(c.saldo)} ${escapar(c.unidade ?? '')}</span>
        </span>
        ${escolhido ? '<span class="li-selo">✓ escolhido</span>' : '<span class="li-selo-esc">usar este</span>'}
      </button>`;
  }

  function ligarDecisao(resultados: LinhaCasada[]): void {
    const escolhas = new Map<number, string>();
    for (const r of resultados) {
      if (r.situacao === 'certo') escolhas.set(r.indice, r.escolhido!.codigo);
    }
    const foraDeProposito = new Set<number>();

    /**
     * A escolha veio da BUSCA (o item não estava entre os propostos — 02/09,
     * Lucas): a linha fecha igual a um candidato clicado, e numa linha
     * "não achei" a quantidade aparece para conferir.
     */
    const escolherBuscado = (indice: number, item: ItemEscolhido): void => {
      escolhas.set(indice, item.codigo);
      foraDeProposito.delete(indice);
      const linha = raiz.querySelector<HTMLElement>(`.li-linha[data-i="${indice}"]`)!;
      linha.classList.remove('li-duvida', 'li-perdida');
      linha.classList.add('li-certa');
      linha.querySelector<HTMLElement>('.li-escolha')!.innerHTML = candidatoHtml(
        { codigo: item.codigo, nome: item.nome, unidade: item.unidade, saldo: item.saldo, foto: item.foto, semelhanca: 0 },
        indice,
        true,
      );
      const qtd = linha.querySelector<HTMLElement>('.li-qtd');
      if (qtd) qtd.hidden = false;
    };
    raiz.querySelectorAll<HTMLElement>('[data-buscar]').forEach((caixa) => {
      const indice = Number(caixa.dataset['buscar']);
      montarBuscaItem(caixa, (item) => escolherBuscado(indice, item), 'Procurar outro item…');
    });

    raiz.querySelector<HTMLElement>('#li-linhas')!.addEventListener('click', (e) => {
      const alvo = e.target as HTMLElement;
      const cand = alvo.closest<HTMLElement>('[data-cand]:not([disabled])');
      if (cand) {
        const i = Number(cand.dataset['cand']);
        escolhas.set(i, cand.dataset['cod']!);
        foraDeProposito.delete(i);
        // Marca visualmente a escolha dentro da linha.
        const linha = cand.closest<HTMLElement>('.li-linha')!;
        linha.classList.remove('li-duvida');
        linha.classList.add('li-certa');
        linha.querySelectorAll<HTMLElement>('[data-cand]').forEach((b) => {
          const meu = b === cand;
          b.classList.toggle('li-cand-ok', meu);
          if (meu) b.setAttribute('disabled', '');
          else b.style.display = 'none';
        });
        linha.querySelector<HTMLElement>('[data-nenhum]')?.remove();
        linha.querySelector<HTMLElement>('.li-pergunta')?.remove();
        return;
      }
      const nenhum = alvo.closest<HTMLElement>('[data-nenhum]');
      if (nenhum) {
        const i = Number(nenhum.dataset['nenhum']);
        foraDeProposito.add(i);
        escolhas.delete(i);
        const linha = nenhum.closest<HTMLElement>('.li-linha')!;
        linha.classList.remove('li-duvida');
        linha.classList.add('li-perdida');
        linha.querySelector<HTMLElement>('.li-escolha')!.innerHTML =
          `<p class="li-nada">Fica de fora — vai anotado na observação do pedido.</p>`;
      }
    });

    raiz.querySelector('#li-carrinho')!.addEventListener('click', () => {
      void paraOCarrinho(resultados, escolhas, foraDeProposito);
    });
  }

  async function paraOCarrinho(
    resultados: LinhaCasada[],
    escolhas: Map<number, string>,
    foraDeProposito: Set<number>,
  ): Promise<void> {
    const erro = raiz.querySelector<HTMLElement>('#li-erro')!;
    const obra = obraAtual();
    if (!obra) {
      erro.textContent = 'Escolha a obra primeiro — o botão 🏗️ lá em cima.';
      erro.hidden = false;
      return;
    }

    const pendentes = resultados.filter(
      (r) => r.situacao === 'duvidoso' && !escolhas.has(r.indice) && !foraDeProposito.has(r.indice),
    );
    if (pendentes.length) {
      erro.textContent = `Ainda há ${pendentes.length} linha(s) esperando você escolher o item — ou marcar "nenhum destes".`;
      erro.hidden = false;
      return;
    }

    const botao = raiz.querySelector<HTMLButtonElement>('#li-carrinho')!;
    botao.disabled = true;
    botao.textContent = 'Adicionando…';

    let adicionadas = 0;
    try {
      for (const r of resultados) {
        const codigo = escolhas.get(r.indice);
        if (!codigo) continue;
        const qtd =
          Number(raiz.querySelector<HTMLInputElement>(`[data-qtd="${r.indice}"]`)?.value) ||
          r.quantidade;
        await adicionar(obra.codigo, codigo, qtd);
        adicionadas++;
      }
    } catch (e) {
      botao.disabled = false;
      botao.textContent = 'Adicionar ao carrinho';
      erro.textContent = `${e instanceof ErroApi ? e.message : 'Falhou no meio.'} ${adicionadas} linha(s) já tinham entrado — confira o carrinho antes de tentar de novo.`;
      erro.hidden = false;
      return;
    }

    // O que não foi encontrado (ou foi marcado "nenhum destes") vira registro
    // para a observação do envio — a decisão do Lucas de reportar sem perder.
    const fora = resultados
      .filter(
        (r) =>
          (r.situacao === 'nao_achei' && !escolhas.has(r.indice)) || foraDeProposito.has(r.indice),
      )
      .map((r) => r.original);
    registrarNaoEncontrados(fora);

    aoTerminar();
  }
}
